import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import {
  useGetHoldQuery,
  useCreateHoldMutation,
  useUpdateHoldMutation,
  useDeleteHoldMutation,
  useUnholdHoldMutation,
  useLazyGetCustomersQuery,
} from '../../../store/api/salesApi';
import { useLazyGetProductsQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useGetLocationsQuery } from '../../../store/api/inventoryApi';
import { useGetUsersQuery } from '../../../store/api/userApi';
import { useGetPaymentAccountsQuery } from '../../../store/api/purchaseApi';
import { CustomerFormContent } from './CustomerForm';
import { formatItemDisplayName } from '../utils/formatItemDisplayName';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../store/slices/authSlice';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

const DEFAULT_VAT = 5;

function HoldForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const currentUser = useSelector(selectUser);

  const { data: hold, isLoading: loadingHold } = useGetHoldQuery(id, { skip: isNew });
  const [createHold] = useCreateHoldMutation();
  const [updateHold] = useUpdateHoldMutation();
  const [deleteHold] = useDeleteHoldMutation();
  const [unholdHold] = useUnholdHoldMutation();
  const [triggerCustomerSearch, { data: customerSearchData, isFetching: searchingCustomers }] = useLazyGetCustomersQuery();
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] =
    useLazyGetProductsQuery();
  const { data: company } = useGetCurrentCompanyQuery();
  const { data: locations } = useGetLocationsQuery();
  const { data: usersData } = useGetUsersQuery();
  const { data: paymentAccounts } = useGetPaymentAccountsQuery();
  const userList = Array.isArray(usersData) ? usersData : usersData?.users ?? [];

  const currency = company?.currency || 'USD';
  const vatIncluded = company?.salesVatIncluded !== false;
  const defaultVat = Number(company?.vatRate) || DEFAULT_VAT;
  const itemNameFormat = company?.printItemNameFormat || 'name_sku';
  const creditDaysOptions = (company?.creditDaysOptions || '7,14,30')
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isInteger(n) && n > 0);

  const [formData, setFormData] = useState({
    customerId: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    orderDate: new Date().toISOString().split('T')[0],
    salesType: 'credit',
    creditDays: '',
    salesPersonId: currentUser?.id || '',
    locationId: '',
    notes: '',
    roundOff: 0,
  });
  const [items, setItems] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [payments, setPayments] = useState([
    { method: 'cash', paymentAccountId: '', referenceNo: '', amount: '' },
  ]);
  const [autoPaymentAmount, setAutoPaymentAmount] = useState(true);
  const [billDiscount, setBillDiscount] = useState(0);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    if (hold && !isNew) {
      setFormData({
        customerId: hold.customerId || '',
        invoiceDate: hold.invoiceDate ? hold.invoiceDate.substring(0, 10) : formData.invoiceDate,
        orderDate: hold.orderDate ? hold.orderDate.substring(0, 10) : formData.orderDate,
        salesType: hold.salesType || 'credit',
        creditDays: hold.creditDays != null ? String(hold.creditDays) : '',
        salesPersonId: hold.salesPersonId || '',
        locationId: hold.locationId || '',
        notes: hold.notes || '',
        roundOff: hold.roundOff || 0,
      });
      const mapped = (hold.items || []).map((it) => ({
        productId: it.productId,
        productName: it.product?.name,
        productSku: it.product?.sku,
        productBrand: it.product?.brand,
        productBarcode: it.product?.barcode,
        quantity: it.quantity,
        unit: it.unit || 'pcs',
        unitPrice: (vatIncluded && (Number(it.vatPercent) || defaultVat) > 0)
          ? Number((it.unitPrice / (1 + (Number(it.vatPercent) || defaultVat) / 100)).toFixed(company?.currencyDecimals ?? 2))
          : it.unitPrice,
        discount: Number(it.discount) || 0,
        vatPercent: Number(it.vatPercent) || defaultVat,
        batchNo: it.batchNo || '',
        expiryDate: it.expiryDate ? it.expiryDate.substring(0, 10) : '',
        productType: it.product?.type || 'product',
        serviceData: it.serviceData ? (typeof it.serviceData === 'string' ? JSON.parse(it.serviceData) : it.serviceData) : null,
      }));
      setItems(mapped);
      setBillDiscount(Number(hold.billDiscount) || 0);
      if (Array.isArray(hold.payments) && hold.payments.length > 0) {
        setPayments(
          hold.payments.map((p) => ({
            method: p.method || 'cash',
            paymentAccountId: p.paymentAccountId || '',
            referenceNo: p.referenceNo || '',
            amount: String(p.amount ?? ''),
          }))
        );
      }
    }
  }, [hold, isNew]);

  useEffect(() => {
    if (customerSearchData?.data) {
      setCustomerSearchOptions(customerSearchData.data);
    } else if (Array.isArray(customerSearchData)) {
      setCustomerSearchOptions(customerSearchData);
    }
  }, [customerSearchData]);

  useEffect(() => {
    if (hold?.customer && !customerSearchOptions.find(c => c.id === hold.customer.id)) {
      setCustomerSearchOptions(prev => [hold.customer, ...prev]);
    }
  }, [hold, customerSearchOptions]);

  const selectedCustomer = customerSearchOptions.find((c) => c.id === formData.customerId) || null;

  useEffect(() => {
    if (isNew && locations?.length && !formData.locationId) {
      const mainWarehouse = locations.find((l) => l.name.toLowerCase().includes('main warehouse'));
      if (mainWarehouse) {
        setFormData((prev) => ({ ...prev, locationId: mainWarehouse.id }));
      } else if (locations.length > 0) {
        setFormData((prev) => ({ ...prev, locationId: locations[0].id }));
      }
    }
  }, [locations, isNew]);


  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const computeLine = (item) => {
    const qty = toNum(item.quantity);
    const price = toNum(item.unitPrice);
    const discount = toNum(item.discount);
    const vatPct = toNum(item.vatPercent);
    const lineSubtotal = Math.max(0, qty * price - discount);
    let vatAmount = 0;
    if (vatPct > 0) {
      vatAmount = lineSubtotal * (vatPct / 100);
    }
    const lineTotal = lineSubtotal + vatAmount;
    return {
      vatAmount: Math.round((vatAmount) * Math.pow(10, typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)) / Math.pow(10, typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2),
      lineTotal: Math.round((lineTotal) * Math.pow(10, typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)) / Math.pow(10, typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2),
    };
  };

  const orderSubtotal = items.reduce(
    (sum, item) => sum + toNum(item.quantity) * toNum(item.unitPrice),
    0
  );
  const lineDiscountTotal = items.reduce((sum, item) => sum + toNum(item.discount), 0);
  const orderDiscountTotal = lineDiscountTotal + toNum(billDiscount);
  const orderVatTotal = items.reduce((sum, item) => sum + computeLine(item).vatAmount, 0);
  const netOrder = orderSubtotal - orderDiscountTotal;
  const grandTotal = netOrder + orderVatTotal;
  const roundOff = toNum(formData.roundOff);
  const totalAmount = grandTotal + roundOff;
  const paidTotal = payments.reduce(
    (sum, p) => sum + (Number.isFinite(Number(p.amount)) ? Number(p.amount) : 0),
    0
  );
  const remaining = totalAmount - paidTotal;
  const change = paidTotal - totalAmount;

  // Auto-sync first payment for cash sales if not manually edited
  useEffect(() => {
    if (
      formData.salesType === 'cash' &&
      payments.length === 1 &&
      autoPaymentAmount
    ) {
      const target = totalAmount.toFixed(company?.currencyDecimals ?? 2);
      if (payments[0].amount !== target) {
        const next = [...payments];
        next[0] = { ...next[0], amount: target };
        setPayments(next);
      }
    }
  }, [totalAmount, formData.salesType, payments, autoPaymentAmount, company]);

  const handleAddProductRow = (product) => {
    if (!product) return;
    setItems((prev) => {
      const newIndex = prev.length;
      setTimeout(() => {
        const el = document.getElementById(`qty-input-${newIndex}`);
        if (el) {
          el.focus();
          el.select();
        }
      }, 100);
      return [
        ...prev,
        {
          productId: product.id,
          productName: product.name,
          productSku: product.sku,
          productBrand: product.brand,
          productBarcode: product.barcode,
          quantity: 1,
          unit: 'pcs',
          unitPrice: (vatIncluded && defaultVat > 0) 
            ? Number(((product.sellingPrice || 0) / (1 + defaultVat / 100)).toFixed(company?.currencyDecimals ?? 2)) 
            : (product.sellingPrice ?? 0),
          discount: 0,
          vatPercent: defaultVat,
          batchNo: '',
          expiryDate: '',
          productType: product.type || 'product',
          serviceData: product.type === 'service' ? { imei: '', deviceModel: '', complains: '' } : null,
        },
      ];
    });
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const getPayload = () => ({
    ...formData,
    invoiceDate: formData.invoiceDate,
    orderDate: formData.orderDate || formData.invoiceDate,
    salesType: formData.salesType,
    creditDays:
      formData.salesType === 'credit' && formData.creditDays
        ? parseInt(formData.creditDays, 10)
        : undefined,
    salesPersonId: formData.salesPersonId || undefined,
    locationId: formData.locationId || undefined,
    billDiscount: Number(billDiscount) || 0,
    roundOff: Number(formData.roundOff) || 0,
    payments: payments.map((p) => ({
      method: p.method,
      paymentAccountId: p.paymentAccountId || undefined,
      referenceNo: p.referenceNo || undefined,
      amount: Number(p.amount || 0),
    })),
    items: items.map((item) => {
      const { vatAmount } = computeLine(item);
      return {
        productId: item.productId,
        quantity: parseFloat(item.quantity),
        unit: item.unit || undefined,
        unitPrice: vatIncluded && (toNum(item.vatPercent) || defaultVat) > 0 
          ? parseFloat(item.unitPrice) * (1 + (toNum(item.vatPercent) || defaultVat) / 100)
          : parseFloat(item.unitPrice),
        discount: toNum(item.discount),
        vatPercent: toNum(item.vatPercent) || defaultVat,
        vatAmount,
        batchNo: item.batchNo || undefined,
        expiryDate: item.expiryDate || undefined,
        serviceData: item.serviceData ? JSON.stringify(item.serviceData) : null,
      };
    }),
  });

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.invoiceDate) {
      alert('Invoice Date is required');
      return;
    }
    if (!items.length) {
      alert('Please add at least one item');
      return;
    }
    if (items.some((i) => !i.productId)) {
      alert('Please select a product for all items');
      return;
    }
    const payload = getPayload();
    try {
      if (isNew) {
        const created = await createHold(payload).unwrap();
        navigate(created?.id ? `/sales/holds/${created.id}` : '/sales/holds');
      } else {
        await updateHold({ id, ...payload }).unwrap();
        navigate('/sales/holds');
      }
    } catch (err) {
      alert(err?.data?.message || 'Failed to save hold');
    }
  };

  const handleUnhold = async () => {
    if (!formData.customerId) {
      alert('Add a customer before unholding');
      return;
    }
    if (formData.salesType === 'cash' && paidTotal + 0.0001 < totalAmount) {
      alert('Cash sales must be fully paid before unholding');
      return;
    }
    if (!formData.invoiceDate) {
      alert('Invoice Date is required');
      return;
    }
    if (!items.length) {
      alert('Please add at least one item');
      return;
    }
    if (items.some((i) => !i.productId)) {
      alert('Please select a product for all items');
      return;
    }
    try {
      const payload = getPayload();
      await updateHold({ id, ...payload }).unwrap();
      const result = await unholdHold(id).unwrap();
      const orderId = result?.createdOrder?.id;
      if (orderId) navigate(`/sales/orders/${orderId}`);
      else navigate('/sales');
    } catch (err) {
      alert(err?.data?.message || 'Unhold failed');
    }
  };

  const handleDelete = async () => {
    setDeleteConfirmOpen(false);
    try {
      await deleteHold(id).unwrap();
      navigate('/sales/holds');
    } catch (err) {
      alert(err?.data?.message || 'Delete failed');
    }
  };

  if (!isNew && loadingHold) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {isNew ? 'New Hold' : `Edit Hold: ${hold?.holdNumber || id}`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={() => navigate('/sales/holds')}>
            Back to Holds
          </Button>
          {!isNew && (
            <>
              <Button
                variant="contained"
                color="primary"
                disabled={!formData.customerId || (formData.salesType === 'cash' && paidTotal + 0.0001 < totalAmount)}
                onClick={handleUnhold}
              >
                Unhold (convert to order)
              </Button>
              <Button variant="outlined" color="error" onClick={() => setDeleteConfirmOpen(true)}>
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSave}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Autocomplete
                  fullWidth
                  options={customerSearchOptions}
                  loading={searchingCustomers}
                  getOptionLabel={(opt) => opt?.name || ''}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  value={selectedCustomer}
                  onChange={(_, newValue) => {
                    setFormData({ ...formData, customerId: newValue?.id || '' });
                  }}
                  onInputChange={(_, newInputValue) => {
                    if (newInputValue && newInputValue.length >= 2) {
                      triggerCustomerSearch({ search: newInputValue, limit: 20 });
                    } else if (!newInputValue) {
                      triggerCustomerSearch({ limit: 20 });
                    }
                  }}
                  onOpen={() => {
                    if (customerSearchOptions.length === 0) triggerCustomerSearch({ limit: 20 });
                  }}
                  renderInput={(params) => <TextField {...params} label="Customer Name *" />}
                />
                <Button
                  variant="outlined"
                  onClick={() => setCustomerDialogOpen(true)}
                  sx={{ height: 56, minWidth: 44 }}
                  title="Add Customer"
                >
                  <PersonAddAltIcon fontSize="small" />
                </Button>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Customer TRN (if VAT registered)"
                value={selectedCustomer?.trn ?? ''}
                InputProps={{ readOnly: true }}
                placeholder="From customer"
              />
            </Grid>

            {/* Hold Detail Headers */}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Hold Number"
                value={hold?.holdNumber || '–'}
                disabled
                helperText="Generated on save"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Invoice Date *"
                value={formData.invoiceDate}
                onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Sales Type"
                value={formData.salesType}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    salesType: e.target.value,
                    creditDays: e.target.value === 'credit' ? prev.creditDays : '',
                  }))
                }
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
              </TextField>
            </Grid>

            {formData.salesType === 'credit' && (
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  select
                  label="Credit days"
                  value={formData.creditDays}
                  onChange={(e) => setFormData({ ...formData, creditDays: e.target.value })}
                >
                  <MenuItem value="">None</MenuItem>
                  {creditDaysOptions.map((d) => (
                    <MenuItem key={d} value={String(d)}>
                      {d} days
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Sales Person"
                value={formData.salesPersonId}
                onChange={(e) => setFormData({ ...formData, salesPersonId: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {userList.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.firstName && u.lastName ? `${u.firstName} ${u.lastName}` : u.email || u.username}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                select
                label="Branch / Store"
                value={formData.locationId}
                onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {(locations || []).map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>
                    {loc.name} ({loc.code})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Line Items Section */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Line Items
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'center',
                    minWidth: { xs: '100%', sm: 420 },
                    maxWidth: 600,
                  }}
                >
                  <Autocomplete
                    fullWidth
                    size="medium"
                    autoHighlight
                    freeSolo
                    options={searchingProducts ? [] : (productSearchData?.products || [])}
                    loading={searchingProducts}
                    filterOptions={(x) => x}
                    value={null}
                    inputValue={productSearch}
                    getOptionLabel={(option) => {
                      if (!option) return '';
                      if (typeof option === 'string') return option;
                      const label = formatItemDisplayName(
                        { name: option.name, sku: option.sku || option.barcode, brand: option.brand },
                        itemNameFormat === 'name' ? 'name_sku' : itemNameFormat
                      );
                      return label || '';
                    }}
                    onInputChange={(_, value) => {
                      setProductSearch(value);
                      if (value && value.trim().length >= 1) {
                        triggerProductSearch({ search: value.trim(), limit: 20 });
                      }
                    }}
                    onChange={(_, value) => {
                      if (value && typeof value === 'object') {
                        handleAddProductRow(value);
                        setProductSearch('');
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id="product-search-input"
                        label="Scan or search item (name / barcode)"
                        placeholder="Type or paste name / barcode"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const list = searchingProducts ? [] : (productSearchData?.products || []);
                            if (list.length > 0) {
                              const typed = String(productSearch).trim();
                              const firstItem = list[0];
                              if (typed) {
                                const isExactMatch =
                                  String(firstItem.barcode || '').trim() === typed ||
                                  String(firstItem.sku || '').trim() === typed ||
                                  firstItem.name.toLowerCase().includes(typed.toLowerCase());
                                if (!isExactMatch) return;
                              }
                              handleAddProductRow(firstItem);
                              setProductSearch('');
                            }
                          }
                        }}
                        sx={{
                          '& .MuiInputBase-root': {
                            height: 52,
                            borderRadius: 2,
                          },
                        }}
                      />
                    )}
                  />
                  <IconButton
                    title="Scan barcode"
                    sx={{
                      height: 52,
                      width: 52,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <QrCodeScannerIcon />
                  </IconButton>
                </Box>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600, width: 56 }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Product Name *</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Barcode</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Batch No</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Expiry Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Quantity *</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Selling Price *</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Unit Total</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Discount</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>VAT %</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>VAT Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Line Total</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => {
                      const { vatAmount, lineTotal } = computeLine(item);
                      return (
                        <TableRow key={index}>
                          <TableCell sx={{ fontWeight: 500 }}>{index + 1}</TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                              {formatItemDisplayName(
                                { name: item.productName, sku: item.productSku, brand: item.productBrand },
                                itemNameFormat
                              )}
                            </Typography>
                            {company?.enableServiceModule && item.productType === 'service' && (
                              <Box sx={{ mt: 1, p: 1.5, borderRadius: 1.5, bgcolor: '#fbf8ff', border: '1px solid #e1d5e7', maxWidth: 400 }}>
                                <Typography variant="caption" color="secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                                  🔧 Service / Repair Details
                                </Typography>
                                <Grid container spacing={1}>
                                  <Grid size={{ xs: 6 }}>
                                    <TextField
                                      size="small"
                                      label="Device Model"
                                      placeholder="e.g. iPhone 14 Pro"
                                      value={item.serviceData?.deviceModel || ''}
                                      onChange={(e) => {
                                        const currentServiceData = item.serviceData || {};
                                        handleItemChange(index, 'serviceData', {
                                          ...currentServiceData,
                                          deviceModel: e.target.value
                                        });
                                      }}
                                      slotProps={{ input: { style: { fontSize: '0.75rem' } }, inputLabel: { style: { fontSize: '0.75rem' } } }}
                                      fullWidth
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 6 }}>
                                    <TextField
                                      size="small"
                                      label="IMEI / Serial"
                                      placeholder="IMEI or Serial No"
                                      value={item.serviceData?.imei || ''}
                                      onChange={(e) => {
                                        const currentServiceData = item.serviceData || {};
                                        handleItemChange(index, 'serviceData', {
                                          ...currentServiceData,
                                          imei: e.target.value
                                        });
                                      }}
                                      slotProps={{ input: { style: { fontSize: '0.75rem' } }, inputLabel: { style: { fontSize: '0.75rem' } } }}
                                      fullWidth
                                    />
                                  </Grid>
                                  <Grid size={{ xs: 12 }}>
                                    <TextField
                                      size="small"
                                      label="Fault / Complains"
                                      placeholder="Describe issues / diagnostic notes"
                                      value={item.serviceData?.complains || ''}
                                      onChange={(e) => {
                                        const currentServiceData = item.serviceData || {};
                                        handleItemChange(index, 'serviceData', {
                                          ...currentServiceData,
                                          complains: e.target.value
                                        });
                                      }}
                                      slotProps={{ input: { style: { fontSize: '0.75rem' } }, inputLabel: { style: { fontSize: '0.75rem' } } }}
                                      fullWidth
                                      multiline
                                      rows={1}
                                    />
                                  </Grid>
                                </Grid>
                              </Box>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>
                            {item.productBarcode ?? '–'}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={item.batchNo ?? ''}
                              onChange={(e) => handleItemChange(index, 'batchNo', e.target.value)}
                              placeholder="If applicable"
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="date"
                              value={item.expiryDate ?? ''}
                              onChange={(e) => handleItemChange(index, 'expiryDate', e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              sx={{ width: 130 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const current = Number(item.quantity || 0) || 0;
                                  const next = Math.max(0.01, current - 1);
                                  handleItemChange(index, 'quantity', String(next));
                                }}
                                title="Decrease"
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                              <TextField
                                id={`qty-input-${index}`}
                                type="number"
                                size="small"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                sx={{ width: 80 }}
                                inputProps={{ min: 0.01, step: 0.01 }}
                                required
                              />
                              <IconButton
                                size="small"
                                onClick={() => {
                                  const current = Number(item.quantity || 0) || 0;
                                  const next = Math.max(0.01, current + 1);
                                  handleItemChange(index, 'quantity', String(next));
                                }}
                                title="Increase"
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={item.unit ?? 'pcs'}
                              onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                              sx={{ width: 70 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.unitPrice}
                              onChange={(e) => {
                                handleItemChange(index, 'unitPrice', e.target.value);
                                handleItemChange(index, 'inclusivePriceInput', undefined);
                              }}
                              sx={{ width: 100 }}
                              inputProps={{ min: 0, step: 0.01 }}
                              required
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={
                                item.inclusivePriceInput !== undefined
                                  ? item.inclusivePriceInput
                                  : item.unitPrice === ''
                                  ? ''
                                  : Number(
                                      (toNum(item.unitPrice) * (1 + toNum(item.vatPercent) / 100)).toFixed(
                                        typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2
                                      )
                                    ).toString()
                              }
                              onChange={(e) => {
                                const nextInclusive = e.target.value;
                                handleItemChange(index, 'inclusivePriceInput', nextInclusive);
                                if (nextInclusive === '') {
                                  handleItemChange(index, 'unitPrice', '');
                                } else {
                                  const exclusivePrice =
                                    Number(nextInclusive) / (1 + toNum(item.vatPercent) / 100);
                                  handleItemChange(
                                    index,
                                    'unitPrice',
                                    exclusivePrice.toFixed(
                                      typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2
                                    )
                                  );
                                }
                              }}
                              sx={{ width: 100 }}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.discount}
                              onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                              sx={{ width: 80 }}
                              inputProps={{ min: 0 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.vatPercent}
                              onChange={(e) => handleItemChange(index, 'vatPercent', e.target.value)}
                              sx={{ width: 60 }}
                              inputProps={{ min: 0, max: 100 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {vatAmount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {lineTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Hold Footer Summary */}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label="Bill Discount"
                value={billDiscount}
                onChange={(e) => setBillDiscount(Number(e.target.value) || 0)}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Grand Total: {currency} {grandTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  label="Round Off"
                  value={formData.roundOff}
                  onChange={(e) => setFormData({ ...formData, roundOff: e.target.value })}
                  inputProps={{ step: 0.01 }}
                  sx={{ width: 120 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', fontSize: '1.4rem' }}>
                  Total: {currency} {totalAmount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                </Typography>
              </Box>

              {/* Payment Section */}
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 4, mb: 2 }}>
                Payment
              </Typography>
              <TableContainer sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Account (optional)</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{`Amount (${currency})`}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((p, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={p.method}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].method = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ minWidth: 120 }}
                          >
                            <MenuItem value="cash">Cash</MenuItem>
                            <MenuItem value="bank">Bank</MenuItem>
                            <MenuItem value="card">Card</MenuItem>
                            <MenuItem value="cheque">Cheque</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            select
                            size="small"
                            value={p.paymentAccountId || ''}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].paymentAccountId = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ minWidth: 180 }}
                          >
                            <MenuItem value="">Auto (by method)</MenuItem>
                            {paymentAccounts?.map((acc) => (
                              <MenuItem key={acc.id} value={acc.id}>
                                {acc.code} - {acc.name}
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={p.referenceNo || ''}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].referenceNo = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ minWidth: 140 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={p.amount}
                            onChange={(e) => {
                              const next = [...payments];
                              next[index].amount = e.target.value;
                              setPayments(next);
                              setAutoPaymentAmount(false);
                            }}
                            sx={{ width: 120 }}
                            inputProps={{ min: 0, step: 0.01 }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setPayments(payments.filter((_, i) => i !== index));
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            const currentPaid = payments.reduce((sum, p) => sum + toNum(p.amount), 0);
                            const bal = Math.max(0, totalAmount - currentPaid);
                            setPayments((prev) => [
                              ...prev,
                              {
                                method: 'cash',
                                paymentAccountId: '',
                                referenceNo: '',
                                amount: bal.toFixed(company?.currencyDecimals ?? 2),
                              },
                            ]);
                            setAutoPaymentAmount(false);
                          }}
                          sx={{ mt: 1 }}
                        >
                          Add Payment Method
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Paid Total: {currency} {paidTotal.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: remaining > 0 ? 'error.main' : 'success.main' }}>
                  {remaining >= 0 ? `Remaining: ${currency} ${remaining.toFixed(company?.currencyDecimals ?? 2)}` : `Change: ${currency} ${Math.abs(change).toFixed(company?.currencyDecimals ?? 2)}`}
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button type="submit" variant="contained" size="large" sx={{ px: 4, borderRadius: 2 }}>
                  {isNew ? 'Create Hold' : 'Save Hold'}
                </Button>
                <Button type="button" variant="outlined" size="large" onClick={() => navigate('/sales/holds')} sx={{ borderRadius: 2 }}>
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Customer</DialogTitle>
        <DialogContent>
          <CustomerFormContent
            compact
            onCancel={() => setCustomerDialogOpen(false)}
            onSuccess={(created) => {
              setCustomerDialogOpen(false);
              if (created?.id) setFormData((prev) => ({ ...prev, customerId: created.id }));
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete this hold?</DialogTitle>
        <DialogContent>
          <Typography>This cannot be undone. The hold will be removed.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default HoldForm;
